<template>
  <div
    v-if="showTooltips"
    class="review-card-tooltips d-flex align-center ga-2"
  >
    <v-menu
      v-if="showAssessments"
      z-index="110"
      attach=""
      :disabled="!assessmentsTransformed.length"
      :close-on-content-click="false"
      :open-on-hover="!isMobile"
      offset-y
    >
      <template #activator="{ on, attrs }">
        <div
          ref="starsRef"
          v-bind="attrs"
          v-on="on"
        >
          <div
            class="review-card-tooltips__stars d-inline-flex align-center"
            :class="starsModifier"
          >
            <span class="ui-icon-rating-star-filled ui-icon-fz-smaller rating--text" />
            <span class="text-subtitle-2 text--text ml-1">{{ starsValue }}</span>
            <span class="review-card-tooltips__stars-label text-body-2 ml-2">{{ starsLabel }}</span>
          </div>
        </div>
      </template>
      <v-card
        class="pa-5"
        elevation="0"
      >
        <div
          v-for="(assessment, index) in assessmentsTransformed"
          :key="assessment.name"
          :class="{ 'mt-4': index > 0 }"
        >
          <div class="text-no-wrap">
            {{ assessment.name }}
          </div>
          <div class="d-flex">
            <v-icon
              v-for="i in assessment.filledStars"
              :key="`filled-${i}`"
              color="rating"
              :class="{ 'ml-1': i > 1 }"
              small
            >
              ui-icon-rating-star-filled
            </v-icon>
            <v-icon
              v-for="i in assessment.outlineStars"
              :key="`outline-${i}`"
              class="ml-1"
              color="rating"
              small
            >
              ui-icon-rating-star-outline
            </v-icon>
            <div
              class="text-capitalize ml-2"
              :class="assessment.color"
            >
              {{ assessment.status }}
            </div>
          </div>
        </div>
      </v-card>
    </v-menu>

    <v-menu
      v-if="showStatuses"
      z-index="110"
      attach=""
      :nudge-left="isMobile ? statusesTooltipOffset : undefined"
      :close-on-content-click="false"
      :open-on-hover="!isMobile"
      offset-y
      @input="onStatusesTooltipToggle"
    >
      <template #activator="{ on, attrs }">
        <div
          v-bind="attrs"
          v-on="on"
          @mouseenter="updateOffset"
        >
          <div
            class="review-card-tooltips__verification d-inline-flex align-center"
            :aria-label="tStatic('Отзыв проверен')"
          >
            <span
              class="ui-icon-shield-check ui-icon-fz-big"
              aria-hidden="true"
            />
            <span class="text-body-2 ml-1">
              {{ verificationStatuses.length }}
            </span>
          </div>
        </div>
      </template>
      <v-card
        class="text--text"
        width="304"
        elevation="0"
      >
        <div class="pa-4">
          <div class="text-subtitle-1">
            {{ tStatic('Отзыв проверен') }}
          </div>
          <div
            v-for="status in verificationStatuses"
            :key="status"
            class="d-flex mt-2"
          >
            <div v-html="status/* eslint-disable-line vue/no-v-html */" />
            <v-spacer />
            <v-icon color="text-success">
              ui-icon-check
            </v-icon>
          </div>
        </div>
        <v-divider />
        <div class="d-flex align-center text-caption text-secondary--text pa-4">
          <v-icon dense>
            {{ originInfo.icon }}
          </v-icon>
          <div class="ml-3">
            {{ tStatic('Оставлен через') }} {{ originInfo.text }}
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import filterReviewRateToStars from 'common/filters/filterReviewRateToStars'
import useYandexGoals from 'src/composables/useYandexGoals'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'
import {
  getAvgRate,
  getRateAssessments,
  getReviewOrigin,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getRateMeta'
import getStarsLabel, {
  getStarsModifier,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getStarsLabel'

const ACTIVATORS_GAP_WIDTH = 8
const AVG_ASSESSMENT_BOUNDARIES = {
  maxError: -0.4,
  maxWarning: 0.4,
}
const ASSESSMENT_TEXT_COLOR = {
  default: 'text--text',
  error: 'text-error--text',
  warning: 'text-warning--text',
  success: 'text-success--text',
}
const STAR_COUNTS = {
  min: 0,
  max: 5,
}

const props = defineProps<{
  rate: Rate
}>()

const isMobile = useIsMobile()

const { sendYaGoal } = useYandexGoals()
const starsRef = ref<HTMLElement | null>(null)
const statusesTooltipOffset = ref(0)

const avgRate = computed(() => getAvgRate(props.rate))
const verificationStatuses = computed(() => (
  (props.rate.review_verification_statuses || []).map(status => tStatic(status))
))
const isBlocked = computed(() => Boolean(
  props.rate.is_refuted
  || props.rate.is_deleted
  || props.rate.is_hidden_for_verification,
))
const showAssessments = computed(() => (
  !isBlocked.value && avgRate.value != null
))
const showStatuses = computed(() => verificationStatuses.value.length > 0)
const showTooltips = computed(() => showAssessments.value || showStatuses.value)
const assessments = computed(() => (
  showAssessments.value ? getRateAssessments(props.rate) : []
))
const assessmentsTransformed = computed(() => assessments.value.map(assessment => {
  const filledStars = assessment.value === null || assessment.value === undefined
    ? STAR_COUNTS.min
    : assessment.value + 3

  return {
    name: assessment.name ? tStatic(assessment.name) : '',
    status: assessment.assessment_name ? tStatic(assessment.assessment_name) : '',
    color: getAssessmentTextColor(assessment.value),
    filledStars,
    outlineStars: STAR_COUNTS.max - filledStars,
  }
}))
const starsValue = computed(() => {
  if (avgRate.value == null) {
    return ''
  }

  const converted = filterReviewRateToStars(avgRate.value)

  return Number(converted).toFixed(1)
})
const starsLabel = computed(() => (
  avgRate.value == null ? '' : getStarsLabel(avgRate.value)
))
const starsModifier = computed(() => (
  avgRate.value == null ? '' : getStarsModifier(avgRate.value)
))
const origin = computed(() => getReviewOrigin(props.rate))
const originInfo = computed(() => {
  if (origin.value === 'callCenter') {
    return {
      icon: 'ui-icon-phone-call',
      text: tStatic('колл-центр'),
    }
  }

  if (origin.value === 'pdMobile') {
    return {
      icon: 'ui-icon-car-prodoctorov',
      text: tStatic(typeof window !== 'undefined' && window.IS_YDOC ? 'YDoc-мобиль' : 'ПроДокторов-мобиль'),
    }
  }

  return {
    icon: 'ui-icon-site',
    text: tStatic('сайт'),
  }
})

function getAssessmentTextColor(value?: number | null) {
  if (value === null || value === undefined) {
    return ASSESSMENT_TEXT_COLOR.default
  }

  if (value < AVG_ASSESSMENT_BOUNDARIES.maxError) {
    return ASSESSMENT_TEXT_COLOR.error
  }

  if (value <= AVG_ASSESSMENT_BOUNDARIES.maxWarning) {
    return ASSESSMENT_TEXT_COLOR.warning
  }

  return ASSESSMENT_TEXT_COLOR.success
}

function updateOffset() {
  statusesTooltipOffset.value = (starsRef.value?.offsetWidth || 0) + ACTIVATORS_GAP_WIDTH
}

function onStatusesTooltipToggle(value: boolean) {
  if (value) {
    sendYaGoal('reviewCardTooltips__openStatusesTooltip')
  }
}
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.review-card-tooltips {
  &__stars {
    height: 32px;
    padding: 0 8px;
    border-radius: $border-radius-md;
    cursor: default;
    background-color: $ui-kit-bg-warning;

    &-label {
      color: $ui-kit-text-warning;
    }
  }

  &__stars--positive {
    background-color: $ui-kit-bg-success;

    .review-card-tooltips__stars-label {
      color: $ui-kit-text-success;
    }
  }

  &__stars--negative {
    background-color: $ui-kit-bg-error;

    .review-card-tooltips__stars-label {
      color: $ui-kit-text-error;
    }
  }

  &__verification {
    height: 32px;
    padding: 0 8px;
    border-radius: $border-radius-md;
    cursor: default;
    color: $ui-kit-primary;
    background-color: $ui-kit-bg-secondary;
  }
}
</style>
